import React, { useState } from 'react';
import { Mail, Phone, Edit, Trash2, Search, Plus, X, Building2, AlertCircle, RotateCcw } from 'lucide-react';
import { TextInput } from '../../../shared/components/TextInput';
import { CompanyRegistrationForm } from '../components/CompanyRegistrationForm';
import { AuthenticatedImage } from '../../../shared/components/AuthenticatedImage';
import { CompanyEditModal } from '../components/CompanyEditModal';
import { useEmpresas, useDeletarEmpresa, useReativarEmpresa } from '../hooks';
import { authService } from '../../../shared/lib/auth';
import type { Company } from '../../../shared/lib/api';
import { formatCNPJ, onlyCnpjChars } from '../../../shared/utils/validators';

const CompaniesPage: React.FC = () => {
  const isAdmin = authService.getCurrentUser()?.role === 'admin';
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editCompany, setEditCompany] = useState<Company | null>(null);
  const [deleteCompany, setDeleteCompany] = useState<Company | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [reactivateCompany, setReactivateCompany] = useState<Company | null>(null);
  const [isReactivating, setIsReactivating] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const { data, isLoading, isError, error } = useEmpresas();
  const deletarEmpresa = useDeletarEmpresa();
  const reativarEmpresa = useReativarEmpresa();
  const companies = data?.data ?? [];

  const filteredCompanies = companies.filter(company =>
    company.tradeName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    company.corporateName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    onlyCnpjChars(company.cnpj).includes(onlyCnpjChars(searchTerm))
  );

  const showSuccess = (msg: string) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(null), 5000);
  };

  const handleCreateSuccess = () => {
    setShowModal(false);
    showSuccess('Empresa cadastrada com sucesso!');
  };

  const handleEditSuccess = () => {
    setEditCompany(null);
    showSuccess('Empresa atualizada com sucesso!');
  };

  const handleReactivateConfirm = async () => {
    if (!reactivateCompany) return;
    setIsReactivating(true);
    try {
      await reativarEmpresa.mutateAsync(reactivateCompany.id);
      setReactivateCompany(null);
      showSuccess('Empresa reativada com sucesso!');
    } finally {
      setIsReactivating(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteCompany) return;
    setIsDeleting(true);
    setDeleteError(null);
    try {
      await deletarEmpresa.mutateAsync(deleteCompany.id);
      setDeleteCompany(null);
      showSuccess('Empresa inativada com sucesso!');
    } catch (err: any) {
      setDeleteError(err?.response?.data?.message || err?.message || 'Erro ao excluir empresa.');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Empresas</h1>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-5 h-5 mr-2" />
          Nova Empresa
        </button>
      </div>

      {successMessage && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-sm text-green-600">{successMessage}</p>
        </div>
      )}

      <div className="max-w-md mb-4">
        <TextInput
          label="Buscar empresas"
          icon={<Search className="w-5 h-5" />}
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
        />
      </div>

      {isLoading ? (
        <div>Carregando...</div>
      ) : isError ? (
        <div className="text-red-600">Erro: {(error as Error)?.message}</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nome Fantasia</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Razão Social</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">CNPJ</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contato</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredCompanies.map(company => {
                const initials = company.tradeName
                  .split(' ')
                  .slice(0, 2)
                  .map(w => w[0])
                  .join('')
                  .toUpperCase();

                return (
                  <tr key={company.id} className={`hover:bg-gray-50 transition-colors ${!company.isActive ? 'opacity-50' : ''}`}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        {company.logoUrl ? (
                          <AuthenticatedImage
                            src={company.logoUrl}
                            alt={company.tradeName}
                            className="w-9 h-9 rounded-full object-cover flex-shrink-0 border border-gray-200"
                            fallback={(
                              <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                                <span className="text-xs font-semibold text-blue-700">{initials}</span>
                              </div>
                            )}
                          />
                        ) : (
                          <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                            {initials ? (
                              <span className="text-xs font-semibold text-blue-700">{initials}</span>
                            ) : (
                              <Building2 className="w-4 h-4 text-blue-400" />
                            )}
                          </div>
                        )}
                        <span className="text-sm font-medium text-gray-900">{company.tradeName}</span>
                        {!company.isActive && (
                          <span className="ml-1 px-1.5 py-0.5 text-xs font-medium bg-red-100 text-red-700 rounded">
                            Inativa
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{company.corporateName}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCNPJ(company.cnpj)}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="space-y-1">
                        <div className="text-sm font-medium text-gray-900">{company.responsibleName}</div>
                        <div className="flex items-center text-sm text-gray-900">
                          <Mail className="w-4 h-4 mr-2 text-gray-400" />
                          {company.responsibleEmail}
                        </div>
                        <div className="flex items-center text-sm text-gray-900">
                          <Phone className="w-4 h-4 mr-2 text-gray-400" />
                          {company.responsiblePhone}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => setEditCompany(company)}
                          className="p-1 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded transition-colors"
                          title="Editar empresa"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        {isAdmin && !company.isActive && (
                          <button
                            onClick={() => setReactivateCompany(company)}
                            className="p-1 text-green-600 hover:text-green-900 hover:bg-green-50 rounded transition-colors"
                            title="Reativar empresa"
                          >
                            <RotateCcw className="w-4 h-4" />
                          </button>
                        )}
                        {isAdmin && company.isActive && (
                          <button
                            onClick={() => { setDeleteCompany(company); setDeleteError(null); }}
                            className="p-1 text-red-600 hover:text-red-900 hover:bg-red-50 rounded transition-colors"
                            title="Inativar empresa"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal de cadastro */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b">
              <h2 className="text-xl font-bold text-gray-900">Cadastrar Nova Empresa</h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6">
              <CompanyRegistrationForm onSuccess={handleCreateSuccess} />
            </div>
          </div>
        </div>
      )}

      {/* Modal de edição */}
      {editCompany && (
        <CompanyEditModal
          company={editCompany}
          onClose={() => setEditCompany(null)}
          onSuccess={handleEditSuccess}
        />
      )}

      {/* Modal de confirmação de exclusão */}
      {deleteCompany && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-sm w-full p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-2">Inativar Empresa</h2>
            <p className="text-sm text-gray-600 mb-1">
              Tem certeza que deseja inativar a empresa{' '}
              <span className="font-semibold text-gray-900">{deleteCompany.tradeName}</span>?
            </p>
            <p className="text-sm text-red-600 mb-6">
              Os usuários vinculados a esta empresa terão o login bloqueado.
            </p>

            {deleteError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {deleteError}
                </p>
              </div>
            )}

            <div className="flex justify-end gap-3">
              <button
                onClick={() => { setDeleteCompany(null); setDeleteError(null); }}
                disabled={isDeleting}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={isDeleting}
                className={`px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors ${isDeleting ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {isDeleting ? 'Inativando...' : 'Inativar'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Modal de confirmação de reativação */}
      {reactivateCompany && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-sm w-full p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-2">Reativar Empresa</h2>
            <p className="text-sm text-gray-600 mb-6">
              Deseja reativar a empresa{' '}
              <span className="font-semibold text-gray-900">{reactivateCompany.tradeName}</span>?
              Os usuários vinculados voltarão a conseguir fazer login.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setReactivateCompany(null)}
                disabled={isReactivating}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleReactivateConfirm}
                disabled={isReactivating}
                className={`px-4 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors ${isReactivating ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {isReactivating ? 'Reativando...' : 'Reativar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompaniesPage;
